import React, { useState } from "react";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetFooter } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Minus, Plus, Trash2, MessageCircle, CreditCard } from "lucide-react";
import { useCart } from "@/context/CartContext";
import { useLang } from "@/context/LanguageContext";
import { STRINGS } from "@/i18n";

const FREE_FROM = 40;
const SHIP_FEE = 8;

export default function CartDrawer() {
  const { items, open, setOpen, updateQty, removeItem, total, checkoutWhatsApp, checkoutStripe, itemName } = useCart();
  const { lang } = useLang();
  const t = STRINGS[lang].cart;

  const [customer, setCustomer] = useState({ name: "", phone: "", address: "" });
  const [showError, setShowError] = useState(false);
  const [loading, setLoading] = useState(false);
  const set = (k, v) => setCustomer((c) => ({ ...c, [k]: v }));

  const isValid = customer.name.trim() && customer.phone.trim() && customer.address.trim();
  const shipping = total >= FREE_FROM || total === 0 ? 0 : SHIP_FEE;
  const grandTotal = total + shipping;

  const handleCheckout = async (fn) => {
    if (!isValid) { setShowError(true); return; }
    setShowError(false);
    if (fn === checkoutStripe) {
      setLoading(true);
      await fn(customer);
      setLoading(false);
    } else {
      fn(customer);
    }
  };

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetContent data-testid="cart-drawer" className="bg-card border-border w-full sm:max-w-md flex flex-col">
        <SheetHeader>
          <SheetTitle className="font-display text-2xl">{t.title}</SheetTitle>
        </SheetHeader>

        <div className="flex-1 overflow-y-auto py-6 space-y-5">
          {items.length === 0 && (
            <p data-testid="empty-cart-msg" className="text-muted-foreground text-sm mt-10 text-center">{t.empty}</p>
          )}
          {items.map((i) => (
            <div key={i.id} data-testid={`cart-item-${i.id}`} className="flex gap-4">
              <img src={i.image_url} alt={itemName(i)} className="h-20 w-20 object-cover rounded-md border border-white/10" />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium truncate">{itemName(i)}</p>
                <p className="text-sm text-muted-foreground font-mono">${i.price.toFixed(2)}</p>
                <div className="flex items-center gap-2 mt-2">
                  <button onClick={() => updateQty(i.id, i.qty - 1)} className="p-1 border border-border rounded hover:bg-secondary" data-testid={`decrease-${i.id}`}><Minus className="h-3 w-3" /></button>
                  <span className="text-sm w-6 text-center font-mono">{i.qty}</span>
                  <button onClick={() => updateQty(i.id, i.qty + 1)} className="p-1 border border-border rounded hover:bg-secondary" data-testid={`increase-${i.id}`}><Plus className="h-3 w-3" /></button>
                  <button onClick={() => removeItem(i.id)} className="p-1 ml-auto text-muted-foreground hover:text-destructive" data-testid={`remove-${i.id}`}><Trash2 className="h-4 w-4" /></button>
                </div>
              </div>
            </div>
          ))}

          {items.length > 0 && (
            <div className="border-t border-border pt-5 space-y-3">
              <p className="text-overline">{t.formTitle}</p>
              <div className="space-y-1.5">
                <Label htmlFor="cust-name" className="text-xs">{t.fName}</Label>
                <Input id="cust-name" data-testid="customer-name-input" value={customer.name} onChange={(e) => set("name", e.target.value)} className="bg-secondary border-border" />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="cust-phone" className="text-xs">{t.fPhone}</Label>
                <Input id="cust-phone" data-testid="customer-phone-input" type="tel" value={customer.phone} onChange={(e) => set("phone", e.target.value)} className="bg-secondary border-border" />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="cust-address" className="text-xs">{t.fAddress}</Label>
                <Textarea id="cust-address" data-testid="customer-address-input" rows={2} value={customer.address} onChange={(e) => set("address", e.target.value)} className="bg-secondary border-border" />
              </div>
              {showError && !isValid && (
                <p data-testid="customer-form-error" className="text-xs text-destructive">{t.fRequired}</p>
              )}
            </div>
          )}
        </div>

        <SheetFooter className="border-t border-border pt-5">
          <div className="w-full space-y-3">
            <div className="space-y-1 text-sm">
              <div className="flex justify-between text-muted-foreground">
                <span>{t.subtotal}</span><span className="font-mono">${total.toFixed(2)}</span>
              </div>
              <div className="flex justify-between text-muted-foreground">
                <span>{t.shippingLabel}</span>
                <span className="font-mono" data-testid="cart-shipping">{shipping === 0 ? t.freeShip : `$${shipping.toFixed(2)}`}</span>
              </div>
              <div className="flex justify-between pt-1">
                <span className="text-muted-foreground">{t.total}</span>
                <span className="font-display text-xl" data-testid="cart-total">${grandTotal.toFixed(2)}</span>
              </div>
            </div>

            <Button
              data-testid="stripe-checkout-button"
              disabled={items.length === 0 || loading}
              onClick={() => handleCheckout(checkoutStripe)}
              className="w-full h-12 bg-primary text-primary-foreground hover:bg-white/90 font-semibold uppercase tracking-wide gap-2 disabled:opacity-40 disabled:cursor-not-allowed"
            >
              <CreditCard className="h-5 w-5" /> {loading ? "…" : t.stripe}
            </Button>
            <Button
              data-testid="whatsapp-checkout-button"
              disabled={items.length === 0}
              onClick={() => handleCheckout(checkoutWhatsApp)}
              variant="outline"
              className="w-full h-12 border-[#25D366]/50 text-[#25D366] bg-transparent hover:bg-[#25D366]/10 font-semibold uppercase tracking-wide gap-2 disabled:opacity-40 disabled:cursor-not-allowed"
            >
              <MessageCircle className="h-5 w-5" /> {t.checkout}
            </Button>
            <p className="text-xs text-muted-foreground text-center">{t.stripeNote}</p>
          </div>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
}
